// src/components/ServicesTable.tsx
import React, { useEffect, useState } from "react";
import { TablePagination, Box, LinearProgress } from "@mui/material";
import {
  fetchServices,
  deleteService,
  updateService,
  addService,
  suggestAiNotes,
  Service,
  SuggestedAiNotesDraft,
} from "../api/services";
import { fetchStaff, Staff } from "../api/staff";
import {
  fetchServiceCategories,
  ServiceCategory,
} from "../api/serviceCategories";
import ServiceCategorySelect from "./ServiceCategorySelect";
import StaffChipSelect from "./StaffChipSelect";
import { useAuth } from "../context/AuthContext";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
} from "@mui/material";

type NewServiceForm = Omit<
  Service,
  | "service_id"
  | "staff_ids"
  | "staff_members"
  | "category_name"
  | "duration_minutes"
  | "price"
> & {
  duration_minutes: string;
  price: string;
  category_id: string | null;
};

const ServicesTable: React.FC = () => {
  const { businessId } = useAuth();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // ✅ State for editing a service
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [aiNotesDraft, setAiNotesDraft] = useState<SuggestedAiNotesDraft | null>(null);
  const [suggestingAiNotes, setSuggestingAiNotes] = useState(false);
  const [bulkAiNotesOpen, setBulkAiNotesOpen] = useState(false);
  const [bulkAiNotesRunning, setBulkAiNotesRunning] = useState(false);
  const [bulkAiNotesProgress, setBulkAiNotesProgress] = useState({
    completed: 0,
    failed: 0,
    total: 0,
  });
  const [bulkAiNotesSummary, setBulkAiNotesSummary] = useState<string | null>(null);

  // ✅ State for adding a new service
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newService, setNewService] = useState<NewServiceForm>({
    service_name: "",
    description: "",
    ai_recommendation_notes: "",
    duration_minutes: "",
    price: "",
    category_id: null,
  });

  // ✅ State for deleting a service
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);

  // ✅ Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // 🧍‍♀️ All staff for linking
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState<string | null>(null);

  // 🧩 Categories
  const [categories, setCategories] = useState<ServiceCategory[]>([]);

  // ✅ Fetch services + categories on mount / business change
  useEffect(() => {
    if (!businessId) return;

    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const [svcData, catData] = await Promise.all([
          fetchServices(),
          fetchServiceCategories(),
        ]);
        setServices(svcData);
        setCategories(catData);
      } catch (err) {
        console.error("🚨 Failed to fetch services or categories:", err);
        setError("Failed to load services.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [businessId]);

  // ✅ Fetch staff for linking
  useEffect(() => {
    if (!businessId) return;

    const loadStaff = async () => {
      try {
        setStaffLoading(true);
        setStaffError(null);
        const data = await fetchStaff();
        setAllStaff(data);
      } catch (err) {
        console.error("🚨 Failed to fetch staff:", err);
        setStaffError("Failed to load staff.");
      } finally {
        setStaffLoading(false);
      }
    };

    loadStaff();
  }, [businessId]);

  // ✅ Handle editing a service
  const handleEdit = (service: Service) => {
    setEditingService(service);
    setAiNotesDraft(null);
  };

  const handleSuggestAiNotes = async () => {
    if (!editingService) return;

    try {
      setSuggestingAiNotes(true);
      const draft = await suggestAiNotes(editingService);
      setAiNotesDraft(draft);
    } catch (error) {
      console.error("🚨 Error suggesting AI notes:", error);
      alert("Failed to suggest AI notes. See console for details.");
    } finally {
      setSuggestingAiNotes(false);
    }
  };

  const handleBulkAiNotesOpen = () => {
    setBulkAiNotesProgress({ completed: 0, failed: 0, total: services.length });
    setBulkAiNotesSummary(null);
    setBulkAiNotesOpen(true);
  };

  const handleBulkAiNotesGenerate = async () => {
    const serviceSnapshot = [...services];

    setBulkAiNotesRunning(true);
    setBulkAiNotesSummary(null);
    setBulkAiNotesProgress({
      completed: 0,
      failed: 0,
      total: serviceSnapshot.length,
    });

    let failed = 0;

    for (const service of serviceSnapshot) {
      try {
        const draft = await suggestAiNotes(service);
        const updatedService = {
          ...service,
          ai_recommendation_notes: draft.ai_recommendation_notes,
        };

        await updateService(service.service_id, updatedService);

        setServices((prev) =>
          prev.map((item) =>
            item.service_id === service.service_id ? updatedService : item
          )
        );
      } catch (error) {
        failed += 1;
        console.error("🚨 Error bulk-generating AI notes:", error);
      } finally {
        setBulkAiNotesProgress((prev) => ({
          completed: prev.completed + 1,
          failed,
          total: serviceSnapshot.length,
        }));
      }
    }

    setBulkAiNotesRunning(false);
    setBulkAiNotesSummary(
      `Updated AI notes for ${serviceSnapshot.length - failed} of ${serviceSnapshot.length} services.`
    );
  };

  // ✅ Handle saving edits (includes staff_ids + category_id)
  const handleSave = async () => {
    if (!editingService) return;

    try {
      const response = await updateService(
        editingService.service_id,
        editingService
      );
      if (response) {
        setServices((prev) =>
          prev.map((service) =>
            service.service_id === editingService.service_id
              ? { ...editingService }
              : service
          )
        );
      }
      setEditingService(null);
    } catch (error) {
      console.error("🚨 Error updating service:", error);
    }
  };

  // ✅ Handle deleting a service
  const handleDelete = (service: Service) => {
    setServiceToDelete(service);
    setDeleteConfirmationOpen(true);
  };

  const confirmDelete = async () => {
    console.log("🛑 Confirm Delete called with:", serviceToDelete);

    if (!serviceToDelete || !serviceToDelete.service_id) {
      console.error("🚨 ERROR: Missing serviceToDelete or service_id");
      alert("❌ Error: Service ID is missing. Cannot delete.");
      return;
    }

    try {
      await deleteService(serviceToDelete.service_id);
      setServices((prev) =>
        prev.filter((s) => s.service_id !== serviceToDelete.service_id)
      );
    } catch (error) {
      console.error("🚨 Error deleting service:", error);
    } finally {
      setDeleteConfirmationOpen(false);
      setServiceToDelete(null);
    }
  };

  // ✅ Handle new service input change (text inputs)
  const handleNewServiceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    setNewService((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ✅ Handle adding a new service
  const handleAddService = async () => {
    const parsedService = {
      ...newService,
      duration_minutes: Number(newService.duration_minutes),
      price: Number(newService.price),
    };

    if (
      !parsedService.service_name ||
      !parsedService.description ||
      !parsedService.duration_minutes ||
      !parsedService.price
    ) {
      alert("⚠️ Please fill in all required fields.");
      return;
    }

    if (!businessId) {
      alert("🚨 Business ID is missing. Cannot add service.");
      return;
    }

    try {
      console.log("➕ Adding new service:", parsedService);

      const addedService = await addService(parsedService);

      if (!addedService || !addedService.service_id) {
        alert("🚨 Service addition failed. Try again.");
        return;
      }

      console.log("✅ Service added:", addedService);

      const completeService: Service = {
        service_id: addedService.service_id,
        service_name:
          addedService.service_name || parsedService.service_name,
        description:
          addedService.description || parsedService.description,
        ai_recommendation_notes:
          addedService.ai_recommendation_notes ||
          parsedService.ai_recommendation_notes ||
          null,
        duration_minutes:
          addedService.duration_minutes || parsedService.duration_minutes,
        price: addedService.price || parsedService.price,
        staff_ids: addedService.staff_ids || [],
        category_id:
          addedService.category_id ?? parsedService.category_id ?? null,
        category_name: addedService.category_name ?? null,
      };

      setServices((prev) => [...prev, completeService]);

      setNewService({
        service_name: "",
        description: "",
        ai_recommendation_notes: "",
        duration_minutes: "",
        price: "",
        category_id: null,
      });
      setOpenAddDialog(false);
    } catch (error) {
      console.error("🚨 Failed to add service:", error);
      alert("❌ Failed to add service. Check console.");
    }
  };

  if (loading) return <Typography>Loading services...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <div style={{ flex: 1, padding: "20px" }}>
      <Typography variant="h5" gutterBottom>
        Services
      </Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        <Button variant="contained" onClick={() => setOpenAddDialog(true)}>
          Add Service
        </Button>
        <Button
          variant="outlined"
          onClick={handleBulkAiNotesOpen}
          disabled={services.length === 0 || bulkAiNotesRunning}
        >
          Generate AI notes
        </Button>
      </Box>

      {staffError && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {staffError}
        </Typography>
      )}

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Service Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Duration (mins)</TableCell>
              <TableCell align="center">Price ($)</TableCell>
              {/* NEW: staff column */}
              <TableCell align="center">Staff</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((service) => {
                const staffCount =
                  service.staff_ids?.length ??
                  service.staff_members?.length ??
                  0;

                return (
                  <TableRow key={service.service_id} hover>
                    <TableCell>{service.service_name}</TableCell>
                    <TableCell>
                      {service.category_name || "Uncategorised"}
                    </TableCell>
                    <TableCell>{service.description}</TableCell>
                    <TableCell align="center">
                      {service.duration_minutes}
                    </TableCell>
                    <TableCell align="center">
                      ${(service.price ?? 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      {staffCount > 0
                        ? `${staffCount} assigned`
                        : "None"}
                    </TableCell>
                    <TableCell align="center">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "10px",
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleEdit(service)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          color="error"
                          onClick={() => handleDelete(service)}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={services.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />

      <Dialog
        open={bulkAiNotesOpen}
        onClose={() => {
          if (!bulkAiNotesRunning) setBulkAiNotesOpen(false);
        }}
        fullWidth
      >
        <DialogTitle>Generate AI notes for all services?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will replace the current AI recommendation notes for every service.
            Public service descriptions will not change. You can review and edit each
            service afterward.
          </Typography>
          {bulkAiNotesRunning && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={
                  bulkAiNotesProgress.total
                    ? (bulkAiNotesProgress.completed /
                        bulkAiNotesProgress.total) *
                      100
                    : 0
                }
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {bulkAiNotesProgress.completed} of {bulkAiNotesProgress.total} services processed
                {bulkAiNotesProgress.failed
                  ? `, ${bulkAiNotesProgress.failed} failed`
                  : ""}
              </Typography>
            </Box>
          )}
          {bulkAiNotesSummary && (
            <Alert
              severity={bulkAiNotesProgress.failed ? "warning" : "success"}
              sx={{ mb: 1 }}
            >
              {bulkAiNotesSummary}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {bulkAiNotesSummary ? (
            <Button onClick={() => setBulkAiNotesOpen(false)} color="primary">
              Close
            </Button>
          ) : (
            <>
              <Button
                onClick={() => setBulkAiNotesOpen(false)}
                disabled={bulkAiNotesRunning}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkAiNotesGenerate}
                color="primary"
                disabled={bulkAiNotesRunning || services.length === 0}
              >
                {bulkAiNotesRunning ? "Generating..." : "Generate notes"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ✅ Edit Service Dialog */}
      <Dialog
        open={Boolean(editingService)}
        onClose={() => setEditingService(null)}
        fullWidth
      >
        <DialogTitle>Edit Service</DialogTitle>
        <DialogContent>
          <TextField
            label="Service Name"
            fullWidth
            margin="dense"
            value={editingService?.service_name || ""}
            onChange={(e) =>
              setEditingService((prev) =>
                prev
                  ? { ...prev, service_name: e.target.value }
                  : prev
              )
            }
          />

          {/* 🆕 Category select */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <ServiceCategorySelect
              categories={categories}
              value={editingService?.category_id ?? null}
              onChange={(id) =>
                setEditingService((prev) =>
                  prev ? { ...prev, category_id: id || null } : prev
                )
              }
            />
          </Box>

          <TextField
            label="Public service description"
            fullWidth
            margin="dense"
            multiline
            minRows={2}
            value={editingService?.description || ""}
            helperText="Shown to customers on the booking page. Keep this concise and customer-friendly."
            onChange={(e) =>
              setEditingService((prev) =>
                prev
                  ? { ...prev, description: e.target.value }
                  : prev
              )
            }
          />
          <TextField
            label="AI recommendation notes"
            fullWidth
            margin="dense"
            multiline
            minRows={4}
            value={editingService?.ai_recommendation_notes || ""}
            helperText="Internal only. Used by the assistant to recommend this service. Not shown to customers."
            onChange={(e) =>
              setEditingService((prev) =>
                prev
                  ? { ...prev, ai_recommendation_notes: e.target.value }
                  : prev
              )
            }
          />
          <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              onClick={handleSuggestAiNotes}
              disabled={suggestingAiNotes || !editingService?.service_name?.trim()}
            >
              {suggestingAiNotes ? "Suggesting..." : "Suggest AI notes"}
            </Button>
            <Typography variant="body2" color="text.secondary">
              Generates a draft for review. It will not save or replace notes until you choose to use it.
            </Typography>
          </Box>
          {aiNotesDraft && (
            <Box sx={{ mt: 1.5 }}>
              <Alert severity="info" sx={{ mb: 1 }}>
                AI draft generated. Review and edit before saving.
              </Alert>
              {aiNotesDraft.warnings?.map((warning) => (
                <Typography key={warning} variant="body2" color="text.secondary">
                  {warning}
                </Typography>
              ))}
              <TextField
                label="Suggested AI notes draft"
                fullWidth
                margin="dense"
                multiline
                minRows={5}
                value={aiNotesDraft.ai_recommendation_notes}
                InputProps={{ readOnly: true }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setEditingService((prev) =>
                    prev
                      ? {
                          ...prev,
                          ai_recommendation_notes:
                            aiNotesDraft.ai_recommendation_notes,
                        }
                      : prev
                  )
                }
              >
                Use draft
              </Button>
            </Box>
          )}
          <TextField
            label="Duration (mins)"
            type="number"
            fullWidth
            margin="dense"
            value={editingService?.duration_minutes || ""}
            onChange={(e) =>
              setEditingService((prev) =>
                prev
                  ? {
                      ...prev,
                      duration_minutes: Number(e.target.value),
                    }
                  : prev
              )
            }
          />
          <TextField
            label="Price ($)"
            type="number"
            fullWidth
            margin="dense"
            value={editingService?.price || ""}
            onChange={(e) =>
              setEditingService((prev) =>
                prev
                  ? { ...prev, price: Number(e.target.value) }
                  : prev
              )
            }
          />

          {/* NEW: Staff assignment */}
          <StaffChipSelect
            staff={allStaff}
            loading={staffLoading}
            selectedIds={editingService?.staff_ids || []}
            onChange={(ids) =>
              setEditingService((prev) =>
                prev ? { ...prev, staff_ids: ids } : prev
              )
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingService(null)}>Cancel</Button>
          <Button onClick={handleSave} color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Confirm Delete Dialog */}
      <Dialog
        open={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmationOpen(false)}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Add Service Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        fullWidth
      >
        <DialogTitle>Add New Service</DialogTitle>
        <DialogContent>
          <TextField
            label="Service Name"
            name="service_name"
            fullWidth
            margin="dense"
            value={newService.service_name}
            onChange={handleNewServiceChange}
          />

          {/* 🆕 Category select */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <ServiceCategorySelect
              categories={categories}
              value={newService.category_id ?? null}
              onChange={(id) =>
                setNewService((prev) => ({
                  ...prev,
                  category_id: id,
                }))
              }
            />
          </Box>

          <TextField
            label="Public service description"
            name="description"
            fullWidth
            margin="dense"
            multiline
            minRows={2}
            value={newService.description}
            onChange={handleNewServiceChange}
            helperText="Shown to customers on the booking page. Keep this concise and customer-friendly."
          />
          <TextField
            label="AI recommendation notes"
            name="ai_recommendation_notes"
            fullWidth
            margin="dense"
            multiline
            minRows={4}
            value={newService.ai_recommendation_notes || ""}
            onChange={handleNewServiceChange}
            helperText="Internal only. Used by the assistant to recommend this service. Not shown to customers."
          />
          <TextField
            label="Duration (mins)"
            name="duration_minutes"
            type="number"
            fullWidth
            margin="dense"
            value={newService.duration_minutes}
            onChange={handleNewServiceChange}
          />
          <TextField
            label="Price ($)"
            name="price"
            type="number"
            fullWidth
            margin="dense"
            value={newService.price}
            onChange={handleNewServiceChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAddService} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ServicesTable;
